import React from 'react';
import CharacterTagOverlay from './CharacterTagOverlay';

const ArtworkDisplay = ({
  currentSceneIndex,
  scenes,
  originalArtwork,
  getFullImageUrl,
  showCharacterTags,
  handleImageError,
  handleImageLoad,
  imageLoadErrors,
  onTagCharacters,
  onRefineArtwork,
  onTellAtmosphereAboutArtwork // Add this new prop
}) => {
  const renderArtworkContent = () => {
    if (currentSceneIndex === 0) {
      // First scene: show original artwork only
      return (
        <div className="relative w-full h-full overflow-hidden rounded-2xl shadow-xl" style={{ marginLeft: '2cm' }}>
          <div className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl p-4 border-2 border-amber-200">
            <img
              src={getFullImageUrl(originalArtwork)}
              alt="Selected Artwork"
              className="w-full h-full object-cover rounded-xl border-2 border-amber-300 shadow-lg"
              style={{ maxHeight: '70vh', minHeight: '300px' }}
              onError={(e) => handleImageError(0, originalArtwork)}
              onLoad={() => handleImageLoad(0)}
            />
            {/* Scene number badge for book view consistency */}
            <div className="absolute -top-2 -right-2 w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
              1
            </div>
          </div>
          <CharacterTagOverlay
            artworkUrl={getFullImageUrl(originalArtwork)}
            taggedCharacters={scenes[currentSceneIndex]?.taggedCharacters || []}
            showTags={showCharacterTags}
          />
        </div>
      );
    } else {
      // Scenes 2+: show scene artwork or placeholder
      const currentScene = scenes[currentSceneIndex];
      
      if (imageLoadErrors[currentSceneIndex]?.permanentlyFailed) {
        return (
          <div className="w-full h-full flex flex-col items-center justify-center bg-red-50 rounded-2xl border-2 border-dashed border-red-200" style={{ minHeight: '300px', maxHeight: '70vh', marginLeft: '2cm' }}>
            <span className="text-red-600 text-base text-center px-2 mb-2">
              Failed to load artwork after 3 attempts
            </span>
            <button
              onClick={() => handleImageError(currentSceneIndex, currentScene?.imageUrl, true)}
              className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded-xl shadow transition-all duration-300"
            >
              Retry Loading
            </button>
          </div>
        );
      }
      
      if (imageLoadErrors[currentSceneIndex]) {
        // Show loading state during retry attempts
        return (
          <div className="w-full h-full flex flex-col items-center justify-center bg-amber-50 rounded-2xl border-2 border-dashed border-amber-200" style={{ minHeight: '300px', maxHeight: '70vh', marginLeft: '2cm' }}>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mb-4"></div>
            <span className="text-amber-600 text-base text-center px-2">
              Retrying to load artwork... (Attempt {imageLoadErrors[currentSceneIndex].retryCount}/3)
            </span>
          </div>
        );
      }
      
      if (currentScene?.imageUrl) {
        return (
          <div className="relative w-full h-full overflow-hidden rounded-2xl shadow-xl" style={{ marginLeft: '2cm' }}>
            <div className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl p-4 border-2 border-amber-200">
              <img
                src={getFullImageUrl(currentScene.imageUrl)}
                alt={`Scene ${currentSceneIndex + 1} Artwork`}
                className="w-full h-full object-cover rounded-xl border-2 border-amber-300 shadow-lg"
                style={{ maxHeight: '70vh', minHeight: '300px' }}
                key={`scene-${currentSceneIndex}-${currentScene.imageUrl}`}
                onError={(e) => handleImageError(currentSceneIndex, currentScene.imageUrl)}
                onLoad={() => handleImageLoad(currentSceneIndex)}
              />
              {/* Scene number badge for book view consistency */}
              <div className="absolute -top-2 -right-2 w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                {currentSceneIndex + 1}
              </div>
            </div>
            <CharacterTagOverlay
              artworkUrl={getFullImageUrl(currentScene.imageUrl)}
              taggedCharacters={currentScene?.taggedCharacters || []}
              showTags={showCharacterTags}
            />
          </div>
        );
      } else {
        // No artwork yet
        return (
          <div className="w-full h-full flex flex-col items-center justify-center bg-amber-50 rounded-2xl border-2 border-dashed border-amber-200" style={{ minHeight: '300px', maxHeight: '70vh', marginLeft: '2cm' }}>
            <div className="text-center">
              <span className="text-amber-400 text-lg mb-2">No artwork yet</span>
              <span
                className="text-amber-600 text-base text-center px-2"
                style={{ fontFamily: "'Baloo 2', 'Comic Sans MS', cursive" }}
              >
                Imagine your scene! Grab your colors and create your own masterpiece for this part of the story.
              </span>
            </div>
          </div>
        );
      }
    }
  };

  const renderActionButtons = () => {
    const currentScene = scenes[currentSceneIndex];
    
    console.log('=== RENDER ACTION BUTTONS ===');
    console.log('currentSceneIndex:', currentSceneIndex);
    console.log('onTellAtmosphereAboutArtwork:', onTellAtmosphereAboutArtwork);
    
    if (currentSceneIndex === 0) {
      // First scene: Do NOT show the debug button, only show tag/refine buttons if present
      return (
        <div className="mt-3 w-full max-h-[200px] overflow-y-auto">
          {/* Tag Characters Button - only show if there are tagged characters */}
          {scenes[currentSceneIndex]?.taggedCharacters?.length > 0 && (
            <div className="mb-2">
              <button
                type="button"
                className="w-full bg-gradient-to-r from-blue-500 to-blue-400 hover:from-blue-600 hover:to-blue-500 text-white font-semibold py-2 px-4 rounded-xl shadow transition-all duration-300"
                onClick={onTagCharacters}
              >
                🏷️ Tag Characters
              </button>
            </div>
          )}

          {/* Refine Artwork Button - only show if there are tagged characters */}
          {scenes[currentSceneIndex]?.taggedCharacters?.length > 0 && (
            <div className="mb-2">
              <button
                type="button"
                className="w-full bg-gradient-to-r from-purple-500 to-purple-400 hover:from-purple-600 hover:to-purple-500 text-white font-semibold py-2 px-4 rounded-xl shadow transition-all duration-300"
                onClick={onRefineArtwork}
              >
                ✨ Refine Artwork
              </button>
            </div>
          )}
        </div>
      );
    } else {
      // Other scenes: Show regular action buttons
      if (!currentScene?.imageUrl) return null;
      
      return (
        <div className="mt-3 w-full max-h-[200px] overflow-y-auto">
          {/* Tag Characters Button */}
          <div className="mb-2">
            <button
              type="button"
              className="w-full bg-gradient-to-r from-blue-500 to-blue-400 hover:from-blue-600 hover:to-blue-500 text-white font-semibold py-2 px-4 rounded-xl shadow transition-all duration-300"
              onClick={onTagCharacters}
            >
              🏷️ Tag Characters
            </button>
          </div>

          {/* Refine Artwork Button */}
          <div className="mb-2">
            <button
              type="button"
              className="w-full bg-gradient-to-r from-purple-500 to-purple-400 hover:from-purple-600 hover:to-purple-500 text-white font-semibold py-2 px-4 rounded-xl shadow transition-all duration-300"
              onClick={onRefineArtwork}
            >
              ✨ Refine Artwork
            </button>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="flex flex-col items-center w-full aspect-square min-h-[250px] sm:min-h-[350px] max-h-[60vh] sm:max-h-[80vh] mb-4 mt-0 lg:mt-14" style={{ marginLeft: '4cm' }}>
      {renderArtworkContent()}
      <div className="text-center text-amber-700 font-semibold mt-2">
        {currentSceneIndex === 0 ? 'Original Artwork' : `Scene ${currentSceneIndex + 1} Artwork`}
      </div>
      {renderActionButtons()}
    </div>
  );
};

export default ArtworkDisplay; 